import { GetServerSideProps } from "next";
import { useState, useEffect, useContext, useCallback, useRef } from "react";

import { HotelAPI } from "../../api/HotelAPI";
import { TextAPI } from "../../api/TextAPI";
import { BestPriceGuarantee } from "../../components/Banners/BestPriceGuarantee/BestPriceGuarantee";
import { FloatingCart } from "../../components/Cart/FloatingCart/FloatingCart";
import { HotelFilter } from "../../components/Hotels/SearchForm/HotelFilter/HotelFilter";
import { SearchForm} from "../../components/Hotels/SearchForm/SearchForm";
import { List } from "../../components/List/List";
import { ListHotelItem } from "../../components/List/ListHotelItem/ListHotelItem";
import { BookingSiteOperated } from "../../components/Misc/BookingSiteOperated/BookingSiteOperated";
import { Usps } from "../../components/Hotels/Usps/Usps";
import { ContinueLink } from "../../components/Misc/ContinueLink/ContinueLink";
import { HotelsSearchRoomsContextType, HotelsSearchRoomsContext } from "../../contexts/HotelSearchRoomsContextWrapper";
import { HotelListType, HotelSearchRoom } from "../../types/HotelType";
import { DatesSearchContext, DatesSearchContextType } from "../../contexts/DatesSearchContext";
import { HotelSearchContextType, HotelSearchContext } from "../../contexts/HotelSearchContext";

import styles from "./index.module.scss";
import { PageView } from "../../components/Analytics/PageView/PageView";
import { CartContext } from "../../contexts/CartContext";
import Head from "next/head";


type IndexPageProps = {
  hotelsServerSideRendering: HotelListType[];
  text: string;
}

function Hotels ( { hotelsServerSideRendering, text } : IndexPageProps){

  const [hotels, setHotels] = useState<HotelListType[]>(hotelsServerSideRendering);
  const [loading, setLoading] = useState<boolean>(false);
  const [showNextStepButton, setShowNextStepButton] = useState<boolean>(false);

  const { filteredHotels, setHotelsSearch } = useContext<HotelsSearchRoomsContextType>(HotelsSearchRoomsContext);
  const { datesSearch } = useContext<DatesSearchContextType>(DatesSearchContext);
  const { hotelSearchRooms } = useContext<HotelSearchContextType>(HotelSearchContext);

  const searchForHotels = useCallback(async (datesSearch:Date[],hotelSearchRooms:HotelSearchRoom[]) => {

    if(hotelSearchRooms.length > 0 && datesSearch.length > 0) {
      setLoading(true);
      const hotelsSearchQuery = new HotelAPI(process.env.NEXT_PUBLIC_API_BASE_URL ?? '');
      const hotelsSearchQueryResult = await hotelsSearchQuery.getHotelsSearch(datesSearch, hotelSearchRooms).finally(() => setLoading(false));
      setHotels(hotelsSearchQueryResult);
      setHotelsSearch(hotelsSearchQueryResult);
      setShowNextStepButton(true)
    }
    else {
      setShowNextStepButton(false)
      setHotels(hotelsServerSideRendering);
    }
  }, [ hotelsServerSideRendering,setHotelsSearch ]);

  const initialised = useRef(false);
  useEffect(() => {
    if(!initialised.current) {
      initialised.current = true;
      searchForHotels(datesSearch,hotelSearchRooms);
    }
  }, [initialised,searchForHotels,datesSearch,hotelSearchRooms]);

  useEffect(() => {
    if(filteredHotels.length > 0) {
      setHotels(filteredHotels)
    }
    if (hotels.length === 0) {
      setShowNextStepButton(false)
    }
  },[filteredHotels, hotels]);

  const hotelNumberPluralizer = (num: number) => {
    return `${num} ${num < 2 ? 'Hotel' : 'Hotels'}`;
  }

  return (
    <>
      <Head key={'hotel-index'}><title>Orlando Hotels & Resorts | Florida Vacation Planning | Universal Orlando™ Hotels</title></Head>
      <PageView pageName={"Hotels"} hotelsOnPage={hotels}/>
      <CartContext.Consumer>
        {({ cart }) => {

          const hotelTitleInCart = cart.data.tickets.map((ticket) => {
            return ticket.data.attraction_title
          })

          return (
            <>
              <main className="products">
              <FloatingCart showNextStep={showNextStepButton} currentSection="hotels" placement="top" />
                <ContinueLink currentSection="hotels" />
                <List title={''} introText={text} rating={""}>
                  <BookingSiteOperated placement="intro" />
                  <Usps />
                  <SearchForm onSubmit={searchForHotels} pageName={'Hotels Index Page'} />
                  {loading ?
                    <div className='loading mh100'></div>
                    :
                    <>
                      {hotels ?
                        <>
                          <div className={styles.hotelResults}>
                            <h2 className={styles.heading}>
                              {hotels.length !== undefined ? `${hotelNumberPluralizer(hotels.length)} Found` : '0 Hotels found'}
                            </h2>
                            <HotelFilter />
                          </div>
                          {hotels.map((hotel: HotelListType, i: number) => {
                            return (
                              <ListHotelItem key={hotel.id} hotel={hotel} hotelTitleInCart={hotelTitleInCart} roomsPage={false}/>
                            );
                          })}
                        </>
                        :
                        <div className={styles.text}>
                          We are having difficulties getting you hotels, Apologies for any inconvenience
                        </div>}
                    </>}
                </List>
                <FloatingCart showNextStep={showNextStepButton} currentSection="hotels" placement="bottom" />
              </main>
              <BestPriceGuarantee />
            </>
          )
        }}
      </CartContext.Consumer>
    </>
  );
};

interface serverSideProps {
  hotelsServerSideRendering: HotelListType[],
  text: string
}

export const getServerSideProps: GetServerSideProps<serverSideProps> = async () => {


  const hotelAPI = new HotelAPI(process.env.NEXT_PUBLIC_API_BASE_URL ?? '');
  const hotels = await hotelAPI.getAll();

  const textAPI = new TextAPI(process.env.NEXT_PUBLIC_API_BASE_URL ?? "");
  const textType = "hotels_body_text";
  const textRes  = await textAPI.getByType(textType);

  let text = "";
  if (textRes === undefined) {
    text = "";
  } else {
    text = textRes!.body;
  }


  const props: IndexPageProps = {
    hotelsServerSideRendering: hotels,
    text
  }

  return { props }
}
export default Hotels;
